import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDateTime } from "@/lib/format";
import { FileUp, Receipt } from "lucide-react";

const MATCH_CLS = {
  zugeordnet: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  mehrdeutig: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
  nicht_zugeordnet: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
};

export default function MfSettlements() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canSettle = can(user?.role, "mobilfunk:settle");
  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef(null);

  const { data } = useQuery({ queryKey: ["mf-settlements"], queryFn: async () => (await api.get("/mobilfunk/settlements")).data });

  const importFile = async () => {
    if (!file) return toast.error("Bitte Datei wählen");
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
      const ft = file.name.toLowerCase().match(/\.xlsx?$/) ? "xlsx" : "csv";
      if (file.name.toLowerCase().endsWith(".pdf")) {
        const { data: h } = await api.post("/mobilfunk/imports/pdf-extract", { filename: file.name, content_base64: b64, import_type: "settlement", period });
        toast.success(`KI-Import: ${h.counts.zugeordnet} zugeordnet, ${h.counts.nicht_zugeordnet} offen`);
      } else {
        const { data: h } = await api.post("/mobilfunk/settlements", { filename: file.name, content_base64: b64, file_type: ft, period });
        toast.success(`Importiert: ${h.counts.zugeordnet} zugeordnet, ${h.counts.nicht_zugeordnet} offen, ${h.counts.doppelt} doppelt`);
      }
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["mf-settlements"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="mf-settlements">
      <PageHeader title="Abrechnungen" description="Tatsächliche Provisionsabrechnungen (CSV/Excel) importieren und Verträgen zuordnen." />
      <SectionCard title="Abrechnung importieren" className="mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5 sm:w-40"><Label>Zeitraum</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-04" data-testid="settlement-period" /></div>
          <div className="flex-1 space-y-1.5"><Label>Datei (CSV / XLSX)</Label>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={!canSettle}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm" data-testid="settlement-file-input" /></div>
          <Button onClick={importFile} disabled={!canSettle || busy || !file} className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="settlement-import"><FileUp className="h-4 w-4" />Importieren</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Spalten: abrechnungsnummer, period, external_contract_no, commission_ref, commission_type, amount, booking_date, direction (gutschrift/belastung), storno_ref. Zuordnung erfolgt über die externe Auftragsnummer.</p>
      </SectionCard>

      <SectionCard title="Importierte Abrechnungen">
        {(data?.items || []).length === 0
          ? <EmptyState icon={Receipt} title="Noch keine Abrechnungen" description="Importierte Abrechnungsdateien erscheinen hier." />
          : <div className="space-y-2">{data.items.map((s) => (
              <div key={s.id} className="flex cursor-pointer items-center justify-between rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40" onClick={() => setDetail(s.id)} data-testid={`settlement-row-${s.id}`}>
                <div><div className="font-medium">{s.filename}</div><div className="text-xs text-muted-foreground">{s.period || "—"} · {s.line_count} Positionen · {fmtDateTime(s.created_at)}</div></div>
                <div className="flex gap-1">
                  <Badge className={MATCH_CLS.zugeordnet}>{s.counts?.zugeordnet || 0} zugeordnet</Badge>
                  <Badge className={MATCH_CLS.nicht_zugeordnet}>{s.counts?.nicht_zugeordnet || 0} offen</Badge>
                </div>
              </div>
            ))}</div>}
      </SectionCard>
      <SettlementDrawer sid={detail} onClose={() => setDetail(null)} canSettle={canSettle} onChanged={() => qc.invalidateQueries({ queryKey: ["mf-settlements"] })} />
    </div>
  );
}

function SettlementDrawer({ sid, onClose, canSettle, onChanged }) {
  const qc = useQueryClient();
  const [matchLine, setMatchLine] = useState(null);
  const { data } = useQuery({ queryKey: ["mf-settlement", sid], queryFn: async () => (await api.get(`/mobilfunk/settlements/${sid}`)).data, enabled: !!sid });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["mf-settlement", sid] }); onChanged(); };
  return (
    <Sheet open={!!sid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="settlement-drawer">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Receipt className="h-4 w-4" />{data?.settlement?.filename}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2">
          {(data?.lines || []).map((l) => (
            <div key={l.id} className="rounded-md border border-border p-3 text-sm" data-testid="settlement-line">
              <div className="flex items-center justify-between">
                <div className="font-medium">{l.external_contract_no || "—"} · {fmtEUR(l.amount)} <span className="text-xs text-muted-foreground">({l.direction})</span></div>
                <Badge className={MATCH_CLS[l.match_status]}>{l.match_status}</Badge>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{l.abrechnungsnummer} · {l.commission_ref || "—"}{l.is_duplicate && <span className="ml-2 text-red-600">doppelt</span>}</div>
              {canSettle && l.match_status !== "zugeordnet" && <Button size="sm" variant="outline" className="mt-2" onClick={() => setMatchLine(l)} data-testid={`match-line-${l.id}`}>Zuordnen</Button>}
            </div>
          ))}
        </div>
        <MatchDialog line={matchLine} onClose={() => setMatchLine(null)} onDone={() => { setMatchLine(null); refresh(); }} />
      </SheetContent>
    </Sheet>
  );
}

function MatchDialog({ line, onClose, onDone }) {
  const [cid, setCid] = useState("");
  const { data } = useQuery({ queryKey: ["mf-contracts-min"], queryFn: async () => (await api.get("/mobilfunk/contracts")).data, enabled: !!line });
  const assign = async () => {
    if (!cid) return toast.error("Vertrag wählen");
    try { await api.post(`/mobilfunk/settlement-lines/${line.id}/match`, { contract_id: cid }); toast.success("Zugeordnet"); setCid(""); onDone(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="match-dialog">
        <DialogHeader><DialogTitle>Position zuordnen</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Vertragsabschluss</Label>
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger data-testid="match-contract-select"><SelectValue placeholder="Vertrag wählen" /></SelectTrigger>
            <SelectContent>{(data?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_name} · {c.external_order_no || c.tariff_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={assign} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="match-save">Zuordnen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
